const PKCE_STORAGE_KEY = 'arventoJourneyCustomerAccountPkce';
const JOURNEY_IDENTITY_EVENT = 'arvento:journey-identity';
let journeySession = null;

export function getJourneySession() {
  return journeySession;
}

export function clearJourneySession() {
  journeySession = null;
}

function notifyJourneyIdentity(target, authenticationStatus, expiresAt = null) {
  if (!target?.dispatchEvent) return;
  const detail = authenticationStatus === 'authenticated'
    ? { authenticationStatus, provider: 'shopify', expiresAt }
    : { authenticationStatus };
  target.dispatchEvent(new CustomEvent(JOURNEY_IDENTITY_EVENT, { detail, bubbles: true }));
}

function base64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis.btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/g, '');
}

function workerOrigin(endpoint) {
  try {
    const url = new URL(endpoint);
    return url.protocol === 'https:' && !url.username && !url.password ? url.origin : '';
  } catch {
    return '';
  }
}

function only(parameters, name) {
  const values = parameters.getAll(name);
  return values.length === 1 ? values[0] : '';
}

function storedTransaction(sessionStorage) {
  try {
    const value = JSON.parse(sessionStorage.getItem(PKCE_STORAGE_KEY) || '');
    return typeof value?.state === 'string' && value.state
      && typeof value?.codeVerifier === 'string' && /^[A-Za-z0-9-._~]{43,128}$/.test(value.codeVerifier)
      ? value
      : null;
  } catch {
    return null;
  }
}

function clearTransaction(sessionStorage) {
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
}

export function createPkceVerifier() {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

export async function createS256Challenge(codeVerifier) {
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))));
}

export async function beginCustomerAccountAuth({ endpoint, sessionStorage, fetch, redirect, createVerifier = createPkceVerifier, createChallenge = createS256Challenge }) {
  const origin = workerOrigin(endpoint);
  if (!origin) throw new Error('AUTH_ENDPOINT_INVALID');
  const codeVerifier = createVerifier();
  const codeChallenge = await createChallenge(codeVerifier);
  const response = await fetch(`${origin}/api/v1/auth/start?code_challenge=${encodeURIComponent(codeChallenge)}`, {
    credentials: 'omit',
    headers: { accept: 'application/json' },
  });
  const payload = await response.json().catch(() => ({}));
  const state = payload?.data?.state;
  const authorizationUrl = payload?.data?.authorizationUrl;
  if (!response.ok || typeof state !== 'string' || !state || typeof authorizationUrl !== 'string' || !authorizationUrl) {
    throw new Error('AUTH_START_FAILED');
  }
  const authorization = new URL(authorizationUrl);
  if (authorization.protocol !== 'https:' || authorization.searchParams.get('state') !== state) {
    throw new Error('AUTH_START_FAILED');
  }
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify({ state, codeVerifier }));
  redirect(authorization.href);
}

export async function completeCustomerAccountAuth({ endpoint, parameters, sessionStorage, fetch, identityTarget = null }) {
  const origin = workerOrigin(endpoint);
  const state = only(parameters, 'state');
  const code = only(parameters, 'code');
  const oauthError = only(parameters, 'error');
  const transaction = storedTransaction(sessionStorage);
  if (!state && !code && !oauthError) {
    clearJourneySession();
    notifyJourneyIdentity(identityTarget, 'unauthenticated');
    return {};
  }
  if (!origin || !transaction || !state || transaction.state !== state || Boolean(code) === Boolean(oauthError)) {
    clearTransaction(sessionStorage);
    clearJourneySession();
    notifyJourneyIdentity(identityTarget, 'error');
    return {};
  }
  if (oauthError) {
    clearTransaction(sessionStorage);
    clearJourneySession();
    notifyJourneyIdentity(identityTarget, 'unauthenticated');
    return { error: 'oauth_error' };
  }
  clearTransaction(sessionStorage);
  try {
    const response = await fetch(`${origin}/api/v1/auth/exchange`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ code, state, code_verifier: transaction.codeVerifier }),
    });
    const payload = await response.json().catch(() => ({}));
    const issuedJourneySession = response.ok ? payload?.data?.journeySession : null;
    if (!issuedJourneySession || typeof issuedJourneySession.accessToken !== 'string' || !issuedJourneySession.accessToken
      || !Number.isSafeInteger(issuedJourneySession.expiresAt)) {
      clearJourneySession();
      notifyJourneyIdentity(identityTarget, 'error');
      return {};
    }
    journeySession = { accessToken: issuedJourneySession.accessToken, expiresAt: issuedJourneySession.expiresAt };
    notifyJourneyIdentity(identityTarget, 'authenticated', journeySession.expiresAt);
    return { journeySession };
  } catch (error) {
    clearJourneySession();
    notifyJourneyIdentity(identityTarget, 'error');
    throw error;
  } finally {
    clearTransaction(sessionStorage);
  }
}

function setStatus(root, text, state = '') {
  const status = root.querySelector('[data-customer-account-auth-status]');
  status.textContent = text;
  status.dataset.state = state;
}

function clearCallbackParameters() {
  const url = new URL(window.location.href);
  ['code', 'state', 'error', 'error_description'].forEach((name) => url.searchParams.delete(name));
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

async function completeCallback(root, button) {
  const parameters = new URLSearchParams(window.location.search);
  if (!['code', 'state', 'error'].some((name) => parameters.has(name))) {
    notifyJourneyIdentity(root, 'unauthenticated');
    return;
  }
  button.disabled = true;
  setStatus(root, 'Completing sign-in…');
  try {
    const result = await completeCustomerAccountAuth({
      endpoint: root.dataset.endpoint,
      parameters,
      sessionStorage: window.sessionStorage,
      fetch: globalThis.fetch,
      identityTarget: root,
    });
    journeySession = result.journeySession || null;
    setStatus(root, journeySession ? 'Signed in.' : result.error ? 'Sign-in was cancelled.' : 'Could not sign in. Please try again.', journeySession ? 'success' : 'error');
  } catch {
    clearJourneySession();
    setStatus(root, 'Could not sign in. Please try again.', 'error');
  } finally {
    clearCallbackParameters();
    button.disabled = false;
  }
}

function initCustomerAccountAuth(root) {
  if (root.dataset.customerAccountAuthInitialized === 'true') return;
  root.dataset.customerAccountAuthInitialized = 'true';
  const button = root.querySelector('[data-customer-account-auth-start]');
  button.addEventListener('click', async () => {
    button.disabled = true;
    setStatus(root, 'Opening Shopify sign-in…');
    try {
      await beginCustomerAccountAuth({
        endpoint: root.dataset.endpoint,
        sessionStorage: window.sessionStorage,
        fetch: globalThis.fetch,
        redirect: (url) => window.location.assign(url),
      });
    } catch {
      setStatus(root, 'Could not start sign-in. Please try again.', 'error');
      notifyJourneyIdentity(root, 'error');
      button.disabled = false;
    }
  });
  completeCallback(root, button);
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-customer-account-auth]').forEach(initCustomerAccountAuth);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.('[data-customer-account-auth]');
    if (root) initCustomerAccountAuth(root);
  });
}
